import React, { useState, useEffect, ChangeEvent } from "react";

import { Upload, X, Check, Trash2 } from "lucide-react";

import { toast, ToastContainer } from "react-toastify";

import "react-toastify/dist/ReactToastify.css";

import "./ImageUploadPage.css";

import {

  removeAdminProfilePicture,

  uploadAdminProfilePicture,

} from "../../services/uploadService";

import { useDispatch, useSelector } from "react-redux";

import { updateProfilePicture } from "../../redux/authSlice";

import { resolveMediaUrl } from "../../utils/mediaUrl";

import { RootState } from "../../redux/store";

import defaultAvatar from "../../assets/images/profile1.png";



interface UploadState {

  selectedImage: File | null;

  previewUrl: string | null;

  uploading: boolean;

  removing: boolean;

  uploadStatus: "idle" | "uploading" | "success" | "error";

}



const AdminProfileUpload: React.FC = () => {

  const dispatch = useDispatch();

  const user = useSelector((state: RootState) => state.auth.user);



  const hasSavedPicture = Boolean(user?.avatarUrl);

  const savedAvatarSrc = hasSavedPicture

    ? resolveMediaUrl(user!.avatarUrl!) || defaultAvatar

    : null;



  const [state, setState] = useState<UploadState>({

    selectedImage: null,

    previewUrl: null,

    uploading: false,

    removing: false,

    uploadStatus: "idle",

  });



  useEffect(() => {

    if (state.selectedImage) return;

    setState((prev) => ({

      ...prev,

      previewUrl: savedAvatarSrc,

    }));

  }, [savedAvatarSrc, state.selectedImage]);



  const handleImageSelect = (e: ChangeEvent<HTMLInputElement>): void => {

    const file = e.target.files?.[0];

    if (!file) return;



    const validTypes = ["image/png", "image/jpeg", "image/jpg"];

    if (!validTypes.includes(file.type)) {

      toast.error("Please select a valid image file (PNG, JPG, or JPEG)");

      return;

    }

    if (file.size > 5 * 1024 * 1024) {

      toast.error("File size must be less than 5MB");

      return;

    }



    const reader = new FileReader();

    reader.onloadend = () => {

      setState((prev) => ({

        ...prev,

        selectedImage: file,

        previewUrl: reader.result as string,

        uploadStatus: "idle",

      }));

    };

    reader.readAsDataURL(file);

    e.target.value = "";

  };



  const handleUpload = async (): Promise<void> => {

    if (!state.selectedImage) return;



    setState((prev) => ({ ...prev, uploading: true }));



    try {

      const response = await uploadAdminProfilePicture(state.selectedImage);



      if (response.status === "success") {

        dispatch(updateProfilePicture(response.avatarUrl));



        const resolved = response.avatarUrl

          ? resolveMediaUrl(response.avatarUrl)

          : null;



        setState((prev) => ({

          ...prev,

          selectedImage: null,

          previewUrl: resolved,

          uploading: false,

          uploadStatus: "success",

        }));



        toast.success("Profile picture saved");

        setTimeout(() => {

          setState((prev) => ({ ...prev, uploadStatus: "idle" }));

        }, 3000);

      } else {

        throw new Error(response.message || "Upload failed");

      }

    } catch (error) {

      console.error("Upload error:", error);

      toast.error("Failed to upload profile picture");

      setState((prev) => ({

        ...prev,

        uploading: false,

        uploadStatus: "error",

      }));

    }

  };



  const handleCancelSelection = (): void => {

    setState((prev) => ({

      ...prev,

      selectedImage: null,

      previewUrl: savedAvatarSrc,

      uploading: false,

      uploadStatus: "idle",

    }));

  };



  const handleRemoveCurrentPicture = async (): Promise<void> => {

    if (!hasSavedPicture || state.removing) return;



    setState((prev) => ({ ...prev, removing: true }));



    try {

      const response = await removeAdminProfilePicture();

      if (response.status === "success") {

        dispatch(updateProfilePicture(null));

        setState({

          selectedImage: null,

          previewUrl: null,

          uploading: false,

          removing: false,

          uploadStatus: "idle",

        });

        toast.success("Profile picture removed");

      } else {

        throw new Error(response.message || "Remove failed");

      }

    } catch (error) {

      console.error("Remove error:", error);

      toast.error("Failed to remove profile picture");

      setState((prev) => ({ ...prev, removing: false }));

    }

  };



  const showEmptyUpload = !state.previewUrl;

  const isPendingUpload = Boolean(state.selectedImage);



  return (

    <div className="upload-container">

      <ToastContainer position="top-right" autoClose={3000} />

      <div className="upload-content">

        <div className="upload-wrapper">

          <div className="upload-card">

            <h2 className="upload-title">Upload Profile Picture</h2>

            <p className="upload-subtitle">

              Upload or remove your admin profile image

            </p>



            <div className="upload-area">

              {showEmptyUpload ? (

                <div className="upload-empty">

                  <div className="upload-icon-circle">

                    <Upload className="upload-icon" />

                  </div>

                  <p className="upload-text-main">

                    Click to upload or drag and drop

                  </p>

                  <p className="upload-text-sub">PNG, JPG or JPEG (max. 5MB)</p>

                  <label className="file-upload-label">

                    <input

                      type="file"

                      className="file-input-hidden"

                      accept="image/png,image/jpeg,image/jpg"

                      onChange={handleImageSelect}

                    />

                    <span className="choose-file-button">Choose File</span>

                  </label>

                </div>

              ) : (

                <div className="upload-preview">

                  <div className="preview-image-wrapper">

                    <img

                      src={state.previewUrl!}

                      alt="Preview"

                      className="preview-image"

                    />

                    {isPendingUpload && (

                      <button

                        onClick={handleCancelSelection}

                        className="preview-remove-button"

                        type="button"

                        title="Cancel selection"

                      >

                        <X className="remove-icon" />

                      </button>

                    )}

                  </div>

                  <p className="preview-filename">

                    {state.selectedImage?.name ||

                      (hasSavedPicture ? "Current profile picture" : "Preview")}

                  </p>

                  {!isPendingUpload && (

                    <label className="file-upload-label preview-change-label">

                      <input

                        type="file"

                        className="file-input-hidden"

                        accept="image/png,image/jpeg,image/jpg"

                        onChange={handleImageSelect}

                      />

                      <span className="choose-file-button choose-file-button--secondary">

                        Choose New Photo

                      </span>

                    </label>

                  )}

                </div>

              )}

            </div>



            {isPendingUpload && (

              <div className="button-container">

                <button

                  onClick={handleUpload}

                  disabled={state.uploading}

                  type="button"

                  className={`upload-button ${state.uploading ? "uploading" : ""}`}

                >

                  {state.uploading ? (

                    <>

                      <div className="loading-spinner"></div>

                      Uploading...

                    </>

                  ) : (

                    <>

                      <Upload className="button-icon" />

                      Upload Image

                    </>

                  )}

                </button>

                <button

                  onClick={handleCancelSelection}

                  type="button"

                  className="cancel-button"

                  disabled={state.uploading}

                >

                  Cancel

                </button>

              </div>

            )}



            {hasSavedPicture && !isPendingUpload && (

              <div className="button-container">

                <button

                  onClick={handleRemoveCurrentPicture}

                  type="button"

                  className="remove-profile-button"

                  disabled={state.removing}

                >

                  {state.removing ? (

                    <>

                      <div className="loading-spinner"></div>

                      Removing...

                    </>

                  ) : (

                    <>

                      <Trash2 className="button-icon" />

                      Remove Current Picture

                    </>

                  )}

                </button>

              </div>

            )}



            {state.uploadStatus === "success" && (

              <div className="message-box success">

                <div className="message-icon success">

                  <Check className="icon" />

                </div>

                <div className="message-content">

                  <p className="message-title">Upload Successful!</p>

                  <p className="message-text">

                    Your profile picture has been updated

                  </p>

                </div>

              </div>

            )}



            {state.uploadStatus === "error" && (

              <div className="message-box error">

                <div className="message-icon error">

                  <X className="icon" />

                </div>

                <div className="message-content">

                  <p className="message-title">Upload Failed</p>

                  <p className="message-text">Please try again later</p>

                </div>

              </div>

            )}

          </div>



          <div className="info-card">

            <h3 className="info-title">Image Requirements:</h3>

            <ul className="info-list">

              <li>• Recommended size: 400x400 pixels</li>

              <li>• Maximum file size: 5MB</li>

              <li>• Accepted formats: PNG, JPG, JPEG</li>

              <li>• Image will be displayed as a circle</li>

              <li>• Use Remove to clear your picture before uploading a new one</li>

            </ul>

          </div>

        </div>

      </div>

    </div>

  );

};



export default AdminProfileUpload;


